import React from 'react'

import LockIcon from '@mui/icons-material/Lock'
import { Box, Button, InputAdornment, Typography } from '@mui/material'
import { BusinessTopicType } from 'constants/enums/TopicTypeEnum'
import { countWords } from 'utils/countWords'

import BusinessTopicChips from 'components/BusinessTopicChips'
import { BaseTextField } from 'components/Form'
import TopicRefinementSection from 'components/TopicRefinementSection'
import WarningMessage from 'components/WarningMessage'

import { commonStyles } from 'styles/common.styles'

import { styles } from './InnovationSection.styles'

export const TEST_ID = 'battle-ships-innovation-section'

export type Props = {
	companyName: string
	companyRisk: string
	createPrompt: () => void
	editable: boolean
	generateScenarioDisabled: () => boolean
	isLoading: boolean
	resetAll: () => void
	setCompanyName: (value: string) => void
	setCompanyRisk: (value: string) => void
	setTopic: (val: BusinessTopicType) => void
	topic: BusinessTopicType
}

const InnovationSection = ({
	topic,
	companyName,
	companyRisk,
	setCompanyRisk,
	setCompanyName,
	setTopic,
	generateScenarioDisabled,
	resetAll,
	isLoading,
	createPrompt,
	editable,
}: Props) => {
	const text =
		'Enter the problem to solve by finishing this phrase: how might we create new ways to...'
	return (
		<>
			<Typography
				variant={'overline'}
				sx={{ ...commonStyles.header, ...styles.header }}
			>
				1. INNOVATION TOPIC
			</Typography>
			<Box display={'flex'}>
				<Box marginRight={'2rem'} sx={styles.sideBar} />
				<Box
					display={'flex'}
					flexDirection={'column'}
					rowGap={'3rem'}
					flexGrow="1"
				>
					<Box
						display={'flex'}
						flexDirection={'column'}
						rowGap={'1rem'}
						flexGrow="1"
					>
						<Typography
							sx={commonStyles.header}
							data-testid={`${TEST_ID}__company-brainstorm-prompt`}
						>
							What company are you brainstorming about today?
						</Typography>
						<BaseTextField
							disabled={!editable}
							InputLabelProps={{
								style: styles.inputLabel,
								required: false,
							}}
							variant="outlined"
							data-testid={`${TEST_ID}__company-name-input`}
							label="Enter company name here."
							value={companyName}
							onChangeValue={(val) => setCompanyName(val)}
							required
							size="medium"
							InputProps={{
								endAdornment: !editable && (
									<InputAdornment position="start">
										<LockIcon />
									</InputAdornment>
								),
							}}
							sx={{
								'& .MuiInputBase-input': {
									boxSizing: 'content-box',
									height: '100%',
								},
								...styles.textField,
							}}
						/>
					</Box>
					<Box
						display={'flex'}
						flexDirection={'column'}
						rowGap={'1rem'}
						flexGrow="1"
					>
						<Typography
							sx={commonStyles.header}
							data-testid={`${TEST_ID}__business-function-prompt`}
						>
							What business function do you want to focus on?
						</Typography>
						<BusinessTopicChips
							innovationTopic={topic}
							setInnovationTopic={setTopic}
							backgroundColor="#FFE0B2"
							editable={editable}
						/>
					</Box>
					<Box
						display={'flex'}
						flexDirection={'column'}
						rowGap={'1rem'}
						flexGrow="1"
						data-testid={`${TEST_ID}__business-problem-prompt-container`}
					>
						<Typography
							sx={commonStyles.header}
							data-testid={`${TEST_ID}__business-problem-prompt`}
						>
							{' '}
							{text}
						</Typography>
						<BaseTextField
							disabled={!editable}
							InputLabelProps={{ style: styles.inputLabel }}
							variant="outlined"
							data-testid={`${TEST_ID}__company-risk-input`}
							label="Finish the phrase: how might we create new ways to ..."
							value={companyRisk}
							onChangeValue={(val) => setCompanyRisk(val)}
							size="medium"
							InputProps={{
								endAdornment: !editable && (
									<InputAdornment position="start">
										<LockIcon />
									</InputAdornment>
								),
							}}
							sx={{
								'& .MuiInputBase-input': {
									boxSizing: 'content-box',
									height: '100%',
								},
								...styles.textField,
							}}
						/>
					</Box>
					<WarningMessage
						prompt={companyRisk}
						color="gameBattleShips"
					/>
					<TopicRefinementSection
						businessProblem={companyRisk}
						companyName={companyName}
						dataTestId={TEST_ID}
					/>
					<Box
						display="flex"
						sx={{
							flexDirection: {
								xs: 'column-reverse',
								sm: 'row',
							},
						}}
						justifyContent="flex"
						gap="2rem"
					>
						<Button
							color="secondary"
							variant="contained"
							data-testid={`${TEST_ID}__create-prompt-button`}
							disabled={generateScenarioDisabled() || isLoading}
							onClick={createPrompt}
							aria-describedby={
								countWords(companyRisk) >= 15
									? 'topic-refinement-education'
									: undefined
							}
						>
							START IDEATING
						</Button>
						<Button
							disabled={!editable}
							variant="text"
							color="secondary"
							data-testid={`${TEST_ID}__reset-all-button`}
							onClick={resetAll}
						>
							RESET ALL FIELDS
						</Button>
					</Box>
				</Box>
			</Box>
		</>
	)
}

export default InnovationSection
